import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';

/* ============================================================================
   COMMUNICATION — the school broadcasting to a group.

   The two things that make this more than a message box:
     1. AUDIENCE. A principal never messages "everyone" — they message the parents
        of Class 9, or the families on Bus 3. Targeting IS the feature.
     2. THE SEND GATE. You cannot unsend 400 SMS messages. So a draft is a draft,
        and before you send we show you EXACTLY who it will reach.
   ========================================================================== */

interface Announcement {
  id: string; title: string; body: string;
  audience: string; audienceIds: string[];
  priority: string; status: string;
  sendEmail: boolean; sendSms: boolean;
  recipientCount: number; readCount: number;
  createdAt: string; sentAt: string | null;
}
interface Preview { count: number; sampleNames: string[]; }

const AUDIENCES = [
  { id: 'AllFamilies', label: 'All families', hint: 'Every parent and student' },
  { id: 'AllStaff', label: 'All staff', hint: 'Teachers, accountants, admins' },
  { id: 'ClassFamilies', label: 'Specific classes', hint: 'Parents of chosen classes' },
  { id: 'RouteFamilies', label: 'Bus routes', hint: 'Families on chosen routes' },
  { id: 'Everyone', label: 'Everyone', hint: 'Staff and families' },
];

export function AnnouncementsPage() {
  const [rows, setRows] = useState<Announcement[] | null>(null);
  const [composing, setComposing] = useState(false);
  const [active, setActive] = useState<Announcement | null>(null);

  const load = useCallback(() => {
    setRows(null);
    api.get<Announcement[]>('/api/announcements').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const columns: Column<Announcement>[] = [
    {
      id: 'title', header: 'Announcement', primary: true, sortable: true,
      value: (a) => a.title,
      cell: (a) => (
        <>
          {a.priority === 'Urgent' && <span style={{ color: 'var(--brand)' }}>⚠ </span>}
          {a.title}
        </>
      ),
    },
    {
      id: 'audience', header: 'Sent to', sortable: true,
      value: (a) => a.audience,
      cell: (a) => (
        <span className="pill pill-muted" style={{ fontSize: 11 }}>
          {AUDIENCES.find((x) => x.id === a.audience)?.label ?? a.audience}
        </span>
      ),
    },
    {
      id: 'channels', header: 'Channels', hideMd: true,
      value: (a) => `${a.sendEmail ? 'email' : ''} ${a.sendSms ? 'sms' : ''}`,
      cell: (a) => (
        <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
          App{a.sendEmail && ' · Email'}{a.sendSms && ' · SMS'}
        </span>
      ),
    },
    {
      id: 'reach', header: 'Reach', align: 'right', sortable: true,
      value: (a) => a.recipientCount,
      cell: (a) => a.status !== 'Sent' ? <span style={{ color: 'var(--ink-3)' }}>—</span> : (
        <span title={`${a.readCount} of ${a.recipientCount} have opened it`}>
          <strong>{a.readCount}</strong>
          <span style={{ color: 'var(--ink-3)' }}> / {a.recipientCount} read</span>
        </span>
      ),
    },
    {
      id: 'status', header: 'Status',
      value: (a) => a.status,
      cell: (a) => (
        <span className={`pill ${a.status === 'Sent' ? 'pill-ok' : a.status === 'Cancelled' ? 'pill-danger' : 'pill-warn'}`}
          style={{ fontSize: 11 }}>
          {a.status}
        </span>
      ),
    },
    {
      id: 'open', header: '', actions: true,
      cell: (a) => (
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setActive(a)}>
          {a.status === 'Draft' ? 'Review & send' : 'Open'}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Communication</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Announcements</h1>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-accent" onClick={() => setComposing(true)}>+ New announcement</button>
        </div>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(a) => a.id}
        search
        searchPlaceholder="Search announcements…"
        onRowClick={(a) => setActive(a)}
        empty={{
          title: 'Nothing sent yet',
          body: 'Announcements you send appear here, with how many people actually opened them.',
          action: <button className="btn btn-accent" onClick={() => setComposing(true)}>+ New announcement</button>,
        }}
      />

      {composing && <Composer onClose={() => setComposing(false)} onDone={() => { setComposing(false); load(); }} />}
      {active && <Detail announcement={active} onClose={() => setActive(null)} onChanged={() => { setActive(null); load(); }} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Composer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [institutions, setInstitutions] = useState<{ id: string; name: string }[]>([]);
  const [classes, setClasses] = useState<{ id: string; label: string }[]>([]);
  const [routes, setRoutes] = useState<{ id: string; name: string }[]>([]);

  const [f, setF] = useState({
    institutionId: '', title: '', body: '',
    audience: 'AllFamilies', audienceIds: [] as string[],
    sendEmail: false, sendSms: false, priority: 'Normal',
  });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/api/institutions')
      .then((r) => { setInstitutions(r); if (r.length === 1) setF((p) => ({ ...p, institutionId: r[0].id })); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!f.institutionId) return;
    api.get<{ id: string; label: string }[]>(`/api/classes?institutionId=${f.institutionId}`)
      .then((r) => setClasses(asList(r))).catch(() => setClasses([]));
    api.get<{ id: string; name: string }[]>('/api/transport/routes')
      .then((r) => setRoutes(asList(r))).catch(() => setRoutes([]));
  }, [f.institutionId]);

  // The dry run. Runs whenever the audience changes, so the number is on screen
  // BEFORE the irreversible button is pressed.
  useEffect(() => {
    if (!f.institutionId) { setPreview(null); return; }
    const needsIds = f.audience === 'ClassFamilies' || f.audience === 'RouteFamilies';
    if (needsIds && f.audienceIds.length === 0) { setPreview(null); return; }

    api.post<Preview>('/api/announcements/preview', {
      institutionId: f.institutionId, audience: f.audience, audienceIds: f.audienceIds,
    }).then(setPreview).catch(() => setPreview(null));
  }, [f.institutionId, f.audience, f.audienceIds]);

  const toggleId = (id: string) =>
    setF((p) => ({
      ...p,
      audienceIds: p.audienceIds.includes(id)
        ? p.audienceIds.filter((x) => x !== id)
        : [...p.audienceIds, id],
    }));

  const save = async () => {
    if (!f.institutionId || !f.title.trim() || !f.body.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/announcements', f);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const needsIds = f.audience === 'ClassFamilies' || f.audience === 'RouteFamilies';
  const options = f.audience === 'ClassFamilies'
    ? classes.map((c) => ({ id: c.id, label: c.label }))
    : routes.map((r) => ({ id: r.id, label: r.name }));

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>New announcement</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13.5, marginBottom: 16 }}>
          This saves as a <strong>draft</strong>. Nothing is sent until you review and send it.
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {institutions.length > 1 && (
            <div>
              <label className="label">Institution</label>
              <select className="input" value={f.institutionId} onChange={(e) => setF({ ...f, institutionId: e.target.value, audienceIds: [] })}>
                <option value="">Select…</option>
                {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Title</label>
            <input className="input" value={f.title} placeholder="School closed tomorrow"
              onChange={(e) => setF({ ...f, title: e.target.value })} />
          </div>

          <div>
            <label className="label">Message</label>
            <textarea className="input" rows={4} value={f.body}
              placeholder="Due to heavy rain, the school will remain closed on Tuesday. Classes resume Wednesday."
              onChange={(e) => setF({ ...f, body: e.target.value })} />
          </div>

          {/* ---- audience ---- */}
          <div>
            <label className="label">Who is this for?</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {AUDIENCES.map((a) => (
                <button key={a.id} type="button" title={a.hint}
                  className={`chip ${f.audience === a.id ? 'is-on' : ''}`}
                  onClick={() => setF({ ...f, audience: a.id, audienceIds: [] })}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {needsIds && (
            <div>
              <label className="label">{f.audience === 'ClassFamilies' ? 'Classes' : 'Routes'}</label>
              {options.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                  None set up yet.
                </p>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {options.map((o) => (
                    <button key={o.id} type="button"
                      className={`chip ${f.audienceIds.includes(o.id) ? 'is-on' : ''}`}
                      onClick={() => toggleId(o.id)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- THE DRY RUN ---- */}
          {preview && (
            <div className="card" style={{ padding: 12, background: 'var(--surface-2)' }}>
              <strong style={{ fontSize: 14 }}>
                This will reach {preview.count} {preview.count === 1 ? 'person' : 'people'}
              </strong>
              {preview.sampleNames.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  {preview.sampleNames.join(', ')}{preview.count > preview.sampleNames.length && ', …'}
                </div>
              )}
              {preview.count === 0 && (
                <div style={{ fontSize: 12, color: 'var(--brand)', marginTop: 4 }}>
                  Nobody matches this audience — check your selection.
                </div>
              )}
            </div>
          )}

          {/* ---- channels ---- */}
          <div>
            <label className="label">How should it go out?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)' }}>
                <input type="checkbox" checked disabled /> In the app (always — it's the record)
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={f.sendEmail} onChange={(e) => setF({ ...f, sendEmail: e.target.checked })} />
                Email
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={f.sendSms} onChange={(e) => setF({ ...f, sendSms: e.target.checked })} />
                SMS
                {f.sendSms && preview && preview.count > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--brand)' }}>
                    — {preview.count} messages, this costs money
                  </span>
                )}
              </label>
            </div>
          </div>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={f.priority === 'Urgent'}
              onChange={(e) => setF({ ...f, priority: e.target.checked ? 'Urgent' : 'Normal' })} />
            Mark as urgent
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !f.title.trim() || !f.body.trim()}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : 'Save as draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Detail({ announcement: a, onClose, onChanged }: {
  announcement: Announcement; onClose: () => void; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const send = async () => {
    setBusy(true); setError(null);
    try { await api.post(`/api/announcements/${a.id}/send`, {}); onChanged(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not send.'); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true); setError(null);
    try { await api.post(`/api/announcements/${a.id}/cancel`, {}); onChanged(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not cancel.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <h2 className="h-display" style={{ fontSize: 20 }}>{a.title}</h2>
          <span className={`pill ${a.status === 'Sent' ? 'pill-ok' : 'pill-warn'}`} style={{ fontSize: 11 }}>
            {a.status}
          </span>
        </div>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <p style={{ color: 'var(--ink-2)', fontSize: 14.5, whiteSpace: 'pre-wrap', marginBottom: 16 }}>{a.body}</p>

        {a.status === 'Sent' && (
          <div className="card" style={{ padding: 14, marginBottom: 16, background: 'var(--surface-2)' }}>
            <strong style={{ fontSize: 15 }}>{a.readCount} of {a.recipientCount} have opened it</strong>
            <div className="dt-foot" style={{ padding: 0, border: 'none', background: 'none', marginTop: 8 }}>
              <div className="bar" style={{ height: 6, borderRadius: 4, background: 'var(--line)', width: '100%', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${a.recipientCount ? (a.readCount / a.recipientCount) * 100 : 0}%`,
                  background: 'var(--brand)',
                }} />
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
              Sent {a.sentAt ? formatDateTime(a.sentAt) : ''}
            </div>
          </div>
        )}

        {a.status === 'Draft' && !confirming && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" disabled={busy} onClick={cancel}>Discard</button>
            <button className="btn btn-accent" disabled={busy} onClick={() => setConfirming(true)} style={{ marginLeft: 'auto' }}>
              Send now
            </button>
          </div>
        )}

        {/* ---- the point of no return ---- */}
        {confirming && (
          <div className="card" style={{ padding: 14, borderColor: 'var(--brand)' }}>
            <strong style={{ fontSize: 14 }}>Send this to everyone in the audience?</strong>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '6px 0 12px' }}>
              This can't be undone. {a.sendSms && <strong>SMS will be sent, which costs money.</strong>}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={() => setConfirming(false)}>Go back</button>
              <button className="btn btn-accent" disabled={busy} onClick={send} style={{ marginLeft: 'auto' }}>
                {busy ? 'Sending…' : 'Yes, send it'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
